import {
  defaultExperience,
  experienceWhenShellAuthUnavailable,
  iframeExpiredUrlForExperience,
  iframeUrlForExperience,
} from './config'
import {
  resolveShellExperience,
  type Dev2222Mode,
  type Experience,
} from './experience'
import { writePersistedExperienceForToken } from './persistedExperience'
import { publishShellAuth } from './shellAuthBridge'
import { resolveShellContactAuth, type ShellContactAuthResult } from './shellContactAuth'

export type ShellEmbedTarget =
  | { kind: 'contact-token'; token: string }
  | { kind: 'expired-page' }

export type ResolvedShellEmbed = {
  experience: Experience
  target: ShellEmbedTarget
  jwtPayload: Record<string, unknown> | null
  jwtPreview: string
  smartAuth?: string
  notice?: string
}

export function shellEmbedIframeSrc(resolved: ResolvedShellEmbed): string {
  if (resolved.target.kind === 'expired-page') {
    return iframeExpiredUrlForExperience(resolved.experience)
  }
  return iframeUrlForExperience(resolved.experience, resolved.target.token)
}

export function resolveShellEmbedWithoutToken(pathExperience?: Experience): ResolvedShellEmbed {
  return {
    experience: experienceWhenShellAuthUnavailable(pathExperience),
    target: { kind: 'contact-token', token: '' },
    jwtPayload: null,
    jwtPreview: '{}',
    notice: 'Missing token in path',
  }
}

function resolveEmbedFailureExperience(
  auth: Extract<ShellContactAuthResult, { kind: 'expired' | 'auth-failed' }>,
  pathExperience: Experience | undefined,
  dev2222Mode: Dev2222Mode,
  token: string,
): Experience {
  if (auth.kind === 'expired') {
    return resolveShellExperience(auth.jwtPayload, pathExperience, dev2222Mode, defaultExperience)
  }
  return experienceWhenShellAuthUnavailable(pathExperience, token)
}

export async function resolveShellEmbedForContactToken(
  token: string,
  pathExperience: Experience | undefined,
  dev2222Mode: Dev2222Mode,
): Promise<ResolvedShellEmbed> {
  const auth = await resolveShellContactAuth(token)

  if (auth.kind === 'ready') {
    const experience = resolveShellExperience(
      auth.jwtPayload,
      pathExperience,
      dev2222Mode,
      defaultExperience,
    )
    publishShellAuth(auth.smartAuth, token, auth.jwtPayload, experience)
    writePersistedExperienceForToken(token, experience)
    return {
      experience,
      target: { kind: 'contact-token', token },
      jwtPayload: auth.jwtPayload,
      jwtPreview: auth.jwtPreview,
      smartAuth: auth.smartAuth,
    }
  }

  if (auth.kind === 'expired' || auth.kind === 'auth-failed') {
    return {
      experience: resolveEmbedFailureExperience(auth, pathExperience, dev2222Mode, token),
      target: { kind: 'expired-page' },
      jwtPayload: null,
      jwtPreview: '{}',
    }
  }

  const experience = experienceWhenShellAuthUnavailable(pathExperience, token)
  return {
    experience,
    target: { kind: 'contact-token', token },
    jwtPayload: null,
    jwtPreview: '{}',
    notice: auth.message,
  }
}
